import { useCallback, useState, useEffect } from 'react'
import { useDropzone, DropzoneOptions, DropEvent, FileRejection } from 'react-dropzone'
import IconButton from '@mui/material/IconButton'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import { styled } from '@mui/material/styles'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTranslation } from 'react-i18next'
import Modal from '@mui/material/Modal'
import Stack from '@mui/material/Stack'

import { toDataURL } from '../../../utils'
import { Box } from '@mui/system'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  maxWidth: { xs: '100%', sm: '90%' },
  maxHeight: { xs: '100%', sm: '90%' },
  width: 'auto',
  height: 'auto',
  overflow: 'auto',
}

const Container = styled('section')<{ error?: boolean }>(({ theme, error }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 4,
  borderColor: error ? theme.palette.error.main : theme.palette.divider,
  borderStyle: 'dashed',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.secondary,
  outline: 'none',
  transition: 'border .24s ease-in-out',
}))
const Aside = styled('aside')(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  marginTop: 16,
}))
const Thumb = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  borderRadius: 2,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: 8,
  marginRight: 8,
  width: 100,
  height: 100,
  padding: 4,
  boxSizing: 'border-box',
  position: 'relative',
}))
const ThumbInner = styled('div')(() => ({
  display: 'flex',
  minWidth: 0,
  overflow: 'hidden',
}))
const Img = styled('img')(() => ({
  display: 'block',
  width: 'auto',
  height: '100%',
  cursor: 'pointer',
}))

type FileData = File & { preview: string; dataUri: string; title: string }

async function customFileGetter(event: DropEvent) {
  const files = []
  const fileList =
    'dataTransfer' in event ? event.dataTransfer!.files : (event.target as HTMLInputElement).files!

  for (let i = 0; i < fileList.length; i++) {
    const file = fileList.item(i)
    if (!file) continue

    const preview = URL.createObjectURL(file)
    const dataUri = await toDataURL(preview)

    Object.defineProperty(file, 'dataUri', { value: dataUri })
    Object.defineProperty(file, 'preview', { value: preview })
    Object.defineProperty(file, 'title', { value: file.name, writable: true })

    files.push(file)
  }

  return files as FileData[]
}

interface Preview {
  preview: string
  name: string
}
interface ThumbPreviewProps {
  file: Preview
  onDelete: (file: Preview) => void
  onClick: (file: Preview) => void
  readOnly: boolean | undefined
}

function ThumbPreview({ file, onDelete, onClick, readOnly }: ThumbPreviewProps) {
  return (
    <Thumb key={file.name}>
      <ThumbInner>
        <Img
          src={file.preview}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            onClick(file)
          }}
        />
        {readOnly ? null : (
          <IconButton
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              onDelete(file)
            }}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </ThumbInner>
    </Thumb>
  )
}

interface FileDB {
  id: number
  title: string
  image: string
  originalName?: string
  type?: string
}
interface DropzoneProps {
  onChange?: (files: FileDB[]) => void
  error?: boolean
  helperText?: string
  defaultValue?: FileDB[]
  onDelete?: (file: FileDB) => Promise<FileDB>
}

export default function Dropzone({
  onChange,
  error,
  helperText,
  onDrop: providedOnDrop,
  defaultValue: givenValue = [],
  onDelete,
  ...props
}: DropzoneOptions & DropzoneProps) {
  const [defaultValue, setDefaultValue] = useState(givenValue)
  const [files, setFiles] = useState<FileData[]>([])
  const [selectedImage, setSelectedImage] = useState<(FileDB & { new: boolean }) | null>(null)
  const { t } = useTranslation('common')

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => {
      const newFiles = [...files, ...(acceptedFiles as FileData[])].filter(
        // Remove duplicates name from acceptedFiles
        (file, index, self) =>
          !!defaultValue.some(f => f.title === file.name) ||
          self.findIndex(f => f.name === file.name) === index
      )
      setFiles(newFiles)
      providedOnDrop?.(newFiles, rejectedFiles, event)
    },
    [defaultValue, files, providedOnDrop]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    ...props,
    getFilesFromEvent: event => customFileGetter(event),
  })

  useEffect(() => {
    onChange?.([
      ...defaultValue,
      ...files.map(file => ({
        id: -1,
        title: file.title,
        image: file.dataUri,
        originalName: file.name,
        type: file.type,
      })),
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, files])

  useEffect(() => {
    return () => files.forEach(file => URL.revokeObjectURL(file.preview))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    setSelectedImage(null)
  }

  return (
    <Stack>
      <Container {...getRootProps()} error={error}>
        <input {...getInputProps()} />
        {!props.disabled && (
          <p>{t('Déposez des fichiers ici, ou cliquez pour sélectionner des fichiers')}</p>
        )}
        <Aside>
          {defaultValue?.map((file, index) => (
            <ThumbPreview
              key={`default_${file.id}`}
              file={{ preview: file.image, name: file.title }}
              onDelete={() => {
                onDelete?.(file)
                  .then(file => {
                    setDefaultValue(defaultValue.filter(f => f.id !== file.id))
                  })
                  .catch(() => {
                    // Do nothing
                  })
              }}
              onClick={file => {
                setSelectedImage({
                  id: index,
                  title: file.name,
                  image: file.preview,
                  new: false,
                })
              }}
              readOnly={props.disabled}
            />
          ))}
          {files.map((file, index) => (
            <ThumbPreview
              key={`new_${index}-${file.name}`}
              file={{
                preview: file.preview,
                name: file.title,
              }}
              onDelete={() => {
                setFiles(
                  files.filter((f, at) => {
                    if (at === index) URL.revokeObjectURL(f.preview)
                    return at !== index
                  })
                )
              }}
              onClick={file => {
                setSelectedImage({
                  id: index,
                  title: file.name,
                  image: file.preview,
                  new: true,
                })
              }}
              readOnly={props.disabled}
            />
          ))}
        </Aside>
      </Container>
      {helperText && <FormHelperText error>{helperText}</FormHelperText>}

      <Modal open={!!selectedImage} onClose={handleClose}>
        {!!selectedImage ? (
          <Stack sx={style} gap="1rem">
            <TextField
              label={t('Titre')}
              value={selectedImage.title}
              variant={'filled'}
              onChange={event => {
                setSelectedImage({
                  ...selectedImage,
                  title: event.target.value,
                })

                if (selectedImage.new) {
                  const newFiles = [...files]
                  newFiles[selectedImage.id].title = event.target.value
                  setFiles(newFiles)
                } else {
                  const newFiles = [...defaultValue]
                  newFiles[selectedImage.id].title = event.target.value
                  setDefaultValue(newFiles)
                }
              }}
              slotProps={{
                input: {
                  readOnly: props.disabled,
                },
              }}
            />
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <img
                src={selectedImage.image}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
              <IconButton
                onClick={() => {
                  window.open(selectedImage.image, '_blank')
                }}
                sx={{
                  position: 'absolute',
                  bottom: '0.5rem',
                  right: '0.5rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  ':hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              >
                <OpenInNewIcon />
              </IconButton>
            </Box>
          </Stack>
        ) : (
          <></>
        )}
      </Modal>
    </Stack>
  )
}
